// Resultat: stjerner, nøgletal og videre. Tabte man, siges det venligt og med en vej frem.

import { atlasFact } from '../../content/atlas-facts';
import type { GridNode, LevelDef, State, ThreatKind } from '../../sim';
import { greenGoal, starMiss } from '../../sim';
import { spareProducer } from '../../ai/truth';
import { slotsFree } from '../../sim/grid';
import { h } from '../dom';
import { ICON } from '../icons';
import { kr } from '../text';
import { SHOW_UNCHECKED, starsRow } from './screens';

export interface ResultHandlers {
  retry(): void;
  next(): void;
  home(): void;
  /** Åbn Netatlasset (vises, når runden låste et nyt kort op). */
  atlas?(): void;
  /** Tabt solo: prøv det tabte år igen fra årets start. */
  retryYear?(): void;
  /** Knaptekst til retryYear, fx "Prøv 2027 igen". */
  retryYearLabel?: string;
}

const SEASON_WHEN = ['om foråret', 'om sommeren', 'om efteråret', 'om vinteren'] as const;

/** Den nærmeste aktive nabo uden flere pladser, hvis en af de to nærmeste var fuld (ellers null). */
function fullNeighbour(state: State, id: string): GridNode | null {
  const n = state.nodes.find((m) => m.id === id);
  if (!n) return null;
  const near = state.nodes
    .filter((m) => m.active && !m.phantom && m.id !== id && m.kind !== 'foreign')
    .sort((p, q) => Math.hypot(p.x - n.x, p.y - n.y) - Math.hypot(q.x - n.x, q.y - n.y))
    .slice(0, 2);
  return near.find((m) => slotsFree(state, m) <= 0) ?? null;
}

/**
 * Hvorfor banen gik tabt, ud fra den sidste mørklægning, og hvad der ville have hjulpet. Rådet skal
 * passe til det, spilleren så: ingen anbefaling af et værk, der allerede kører fuldt, og en fuld nabo
 * siges ved navn (playtest: "fik aldrig en ledning", da Endrup var fuld).
 */
export function lossText(state: State): string {
  const b = state.lastBlackout;
  // Sammen (med makker eller Volt) var pengene fælles.
  const left = state.budget > 0 ? ` ${state.coop ? 'I' : 'Du'} havde ${kr(state.budget)} ubrugt.` : '';
  if (!b) return `Tilliden slap op. Byg to veje til de store byer, så én knækket ledning ikke slukker dem.${left}`;
  const by = state.nodes.find((n) => n.id === b.node)?.name ?? b.node;
  // Banen tabes i finalen eller i et år: finalen har ingen årstid på HUD'en.
  const when = state.finale ? 'i finalen' : (SEASON_WHEN[b.season] ?? '');
  switch (b.cause) {
    case 'unconnected': {
      const full = fullNeighbour(state, b.node);
      if (full) return `${by} gik i sort ${when}, fordi ${full.name} ikke havde flere pladser til ledninger. En station giver flere pladser.${left}`;
      // IGN runde 2: en by med ledninger, men uden en producent i sit net, "fik" sin ledning.
      if (state.lines.some((l) => l.a === b.node || l.b === b.node))
        return `${by} havde ledninger, men de nåede aldrig ud til en mølle eller et værk, så den gik i sort ${when}. Forbind byens lille net med resten.${left}`;
      return `${by} fik ingen ledning og gik i sort ${when}. Træk en ledning til den, så snart den dukker op.${left}`;
    }
    case 'cut': {
      // IGN runde 1: "To veje ind" til en by, der havde tre. Rådet tæller de veje, byen faktisk havde.
      const ways = state.lines.filter((l) => l.a === b.node || l.b === b.node).length;
      if (ways <= 1) return `${by} blev skåret fra af en knækket ledning ${when}. To veje ind til byen og en montør mere holder lyset tændt.${left}`;
      const n = ways === 2 ? 'to' : ways === 3 ? 'tre' : String(ways);
      return `${by} havde ${n} veje ind, men blev alligevel skåret fra ${when}. En vej fra en anden kant af nettet og en montør mere holder lyset tændt.${left}`;
    }
    case 'lines':
      return `${by} voksede fra ${state.coop ? 'jeres' : 'dine'} ledninger ${when}. Opgradér ledningen ind til byen, eller byg en station.${left}`;
    case 'power': {
      // Spor "sandhed": kun en producent, en ledning faktisk kan nå (aldrig Holland).
      const other = spareProducer(state, b.node);
      if (other) return `${by} manglede strøm ${when}. ${other.name} havde strøm til overs: en ledning dertil kunne have dækket toppen.${left}`;
      return `${by} manglede strøm ${when}. Alt, der var koblet på, kørte fuldt. Kobl flere møller og solceller på i god tid.${left}`;
    }
  }
}

/** Tabets overskrift: én by i sort nævnes ved navn; flere byer er et mørkere net. */
export function lossTitle(state: State): string {
  const b = state.lastBlackout;
  const by = b ? state.nodes.find((n) => n.id === b.node)?.name : undefined;
  if (by && state.stats.blackouts <= 1) return `MØRKT I ${by.toUpperCase()}`;
  return 'TILLIDEN SLAP OP';
}

const THREAT_LABEL: Partial<Record<ThreatKind, string>> = {
  storm: 'storme uden brud',
  anchor: 'ankre uden brud',
  digger: 'gravkøer uden brud',
};

/** Banens egne trusler som "X/Y" og en etiket, der navngiver dem. Null, hvis ingen kom. */
export function threatFigure(state: State): { value: string; label: string } | null {
  const faced = state.stats.threatsFaced;
  // Uden ledninger er der intet at bryde, og "2/2 uden brud" ville være en tom ros.
  if (faced.length === 0 || state.stats.linesBuilt === 0) return null;
  const clean = faced.filter((t) => t.clean).length;
  const kinds = new Set(faced.map((t) => t.kind));
  const only = kinds.size === 1 ? THREAT_LABEL[faced[0]!.kind] : undefined;
  return { value: `${clean}/${faced.length}`, label: only ?? 'farer uden brud' };
}

/** Byer med strøm lige nu som "x/y" (tabskærmen). */
export function litFigure(state: State): string {
  let lit = 0;
  let all = 0;
  for (const n of state.nodes) {
    const c = n.city;
    if (!c || !n.active || n.phantom) continue;
    all++;
    if (!c.out && c.demandMw > 0 && c.servedMw >= c.demandMw * 0.9) lit++;
  }
  return `${lit}/${all}`;
}

/** Byerne uden fuld strøm, da banen sluttede, som én sætning. */
export function darkAtEnd(state: State): string {
  const dark = state.nodes.filter((n) => n.city && n.active && !n.phantom && !(!n.city.out && n.city.demandMw > 0 && n.city.servedMw >= n.city.demandMw * 0.9));
  if (dark.length === 0) return '';
  const nm = dark.map((n) => n.name);
  const who = nm.length === 1 ? nm[0] : `${nm.slice(0, -1).join(', ')} og ${nm[nm.length - 1]}`;
  return `${who} manglede strøm til sidst.`;
}

/** Sejr: en lysbølge over kortet og små fyrværkeri-bursts bag stjernerne. Ren pynt, skjult for skærmlæsere. */
function celebration(): HTMLElement {
  return h('div', { class: 'celebrate', 'aria-hidden': 'true' }, h('i', { class: 'wave' }));
}

function withSparks(row: HTMLElement): HTMLElement {
  row.classList.add('sparkle');
  row.append(sparks());
  return row;
}

/** Små lysudbrud lige ved siden af stjernerne (i % af stjernerækken), så de aldrig ligger hen over tekst. */
function sparks(): HTMLElement {
  const bursts: Array<[number, number, string, number]> = [
    [-18, 35, 'var(--yellow)', 0.35],
    [118, 40, 'var(--turquoise)', 0.6],
    [-40, 62, 'var(--green)', 0.85],
    [132, 60, 'var(--orange)', 1.15],
    [-18, 70, 'var(--turquoise)', 1.4],
  ];
  return h(
    'span',
    { class: 'sparks', 'aria-hidden': 'true' },
    ...bursts.map(([x, y, c, d]) => h('i', { class: 'burst', style: `left:${x}%;top:${y}%;--c:${c};--d:${d}s` })),
  );
}

/** To stjerner: sig præcis, hvad den tredje manglede, og hvor (playtest runde 2: "hvorfor kun to?"). */
function missText(state: State, level: LevelDef): string {
  const m = starMiss(state, level);
  const name = m && 'node' in m ? (state.nodes.find((n) => n.id === m.node)?.name ?? 'en by') : '';
  if (m?.why === 'customer') return `Grønt nok. Den tredje stjerne røg, fordi ${name} aldrig kom på nettet.`;
  if (m?.why === 'clock') {
    // Hang byen i én ledning, og har et brud været der, var det bruddet: sig det, og hvad der hjælper.
    const own = state.lines.filter((l) => l.a === m.node || l.b === m.node);
    const hit = own.some((l) => l.status === 'broken' || l.halfDown) || state.stats.breaksRepaired > 0;
    if (own.length <= 1 && hit) return `Grønt nok. Den tredje stjerne røg, fordi ${name} hang i én ledning, og et brud tog den. Giv den to veje ind.`;
    return `Grønt nok. Den tredje stjerne røg, fordi ${name} ventede for længe på strøm.`;
  }
  if (m?.why === 'n1') return `Grønt nok. Den tredje stjerne røg, fordi ${name} kun havde én vej ind. Giv den to.`;
  return 'Grønt nok. Den tredje stjerne kræver, at alle kunder kommer på, og at ingen by venter for længe.';
}

/** waitHost: lad kun værten vælge (ubrugt siden IGN-løkke 2, runde 2, hvor begge makkere må trykke). */
export function resultScreen(
  state: State,
  level: LevelDef,
  hasNext: boolean,
  handlers: ResultHandlers,
  newCard = false,
  waitHost = false,
  team: HTMLElement | null = null,
  /** Kampagnen klaret eller Dagens vagts gitter: står over knapperne. */
  extra: HTMLElement | null = null,
): HTMLElement {
  const r = state.result;
  const won = r?.won ?? false;
  const stars = r?.stars ?? 0;
  const g = Math.round(r?.greenPct ?? 0);
  const goal = greenGoal(level.greenGoalPct, state.difficulty);
  const why =
    !won
      ? lossText(state)
      : stars === 3
        ? 'Alle byer fik strøm, alle kunder kom på, og nettet var grønt. Flot vagt.'
        : stars === 2
          ? missText(state, level)
          : `Byerne fik strøm, men nettet nåede ikke ${goal} % grøn strøm. Flere møller giver flere stjerner.`;
  const threat = threatFigure(state);
  // Vundet, men ikke alle byer har strøm til sidst: sig det ærligt (playtest: "LYSET ER TÆNDT" med
  // byer på 0 %).
  const lit = litFigure(state);
  const [litN, allN] = lit.split('/').map(Number);
  const allLit = litN === allN;
  const fact = atlasFact(level.id, state.seed);
  // Tabt efter første år: det tabte år kan tages om, så man ikke skal spille hele banen igen.
  const yearRetry = !won && !waitHost && !!handlers.retryYear;
  const fig = (value: string, label: string): HTMLElement => h('div', { class: 'figure' }, h('b', {}, value), h('span', {}, label));
  return h(
    'div',
    { class: `card result ${won ? 'won' : 'lost'}` },
    won ? celebration() : null,
    h('p', { class: 'eyebrow' }, `Bane ${level.number} · ${level.title}`),
    h('h1', { class: 'title small' }, won ? (allLit ? 'LYSET ER TÆNDT' : 'BANEN ER KLARET') : lossTitle(state)),
    won ? withSparks(starsRow(stars, { on: ICON.star, off: ICON.starOff }, true)) : null,
    won ? h('p', { class: 'stars-text' }, `${stars} af 3 stjerner`) : null,
    h('p', { class: 'lead' }, won && !allLit ? `${why} ${darkAtEnd(state)}` : why),
    h(
      'div',
      { class: 'figures four' },
      // Et tabt spil uden ledninger var ikke "99 % grønt": dér er byerne med strøm det ærlige tal.
      won ? fig(`${g} %`, 'grøn strøm') : fig(litFigure(state), 'byer med strøm'),
      // Det tal, spilleren kan tælle på kortet: ledningerne i nettet nu (ikke alle køb, også revne).
      fig(String(state.lines.length), 'ledninger i nettet'),
      threat ? fig(threat.value, threat.label) : fig(String(state.stats.breaksRepaired), 'brud repareret'),
      fig(String(state.stats.blackouts), state.stats.blackouts === 1 ? 'udfald' : 'udfald i alt'),
    ),
    team,
    extra,
    h(
      'div',
      { class: 'actions' },
      h('button', { type: 'button', class: 'btn ghost', onclick: () => handlers.home() }, h('span', { html: ICON.home }), 'Forsiden'),
      waitHost
        ? h('p', { class: 'wait-host', role: 'status' }, 'Værten vælger, hvad I gør nu.')
        : h('button', { type: 'button', class: `btn ${(won && hasNext) || yearRetry ? 'secondary' : 'primary'}`, autofocus: !(won && hasNext) && !yearRetry, onclick: () => handlers.retry() }, h('span', { html: ICON.retry }), yearRetry ? 'Forfra' : 'Prøv igen'),
      yearRetry && !waitHost ? h('button', { type: 'button', class: 'btn primary', autofocus: true, onclick: () => handlers.retryYear?.() }, h('span', { html: ICON.retry }), handlers.retryYearLabel ?? 'Prøv året igen') : null,
      won && hasNext && !waitHost ? h('button', { type: 'button', class: 'btn primary next', autofocus: true, onclick: () => handlers.next() }, 'Næste bane', h('span', { html: ICON.next })) : null,
    ),
    fact
      ? h(
          'aside',
          { class: 'fact' },
          h('b', {}, 'Vidste du?', fact.checked || !SHOW_UNCHECKED ? null : h('span', { class: 'badge unchecked', title: 'Ikke faktatjekket af Energinet endnu' }, 'UTJEKKET')),
          h('p', {}, fact.text),
          h('small', {}, `Kilde: ${fact.source}. Spillets tal er forenklede.`),
          newCard && handlers.atlas
            ? h(
                'button',
                { type: 'button', class: 'new-card', onclick: () => handlers.atlas?.() },
                h('span', { html: ICON.book }),
                'Nyt kort i Netatlasset',
              )
            : null,
        )
      : null,
  );
}
